import { supabase } from '../client';
import type { RoleFitProjection, RoleFitTopology } from '@core/types';

const SELECT_LIST = `
  *,
  candidate:ggj_b2b_candidates(id, full_name, email),
  job:ggj_b2b_jobs(id, title, client_id, client:ggj_b2b_clients(id, name))
`;

function mapRow(data: Record<string, unknown>): RoleFitProjection {
  const jobRaw = data.job as Record<string, unknown> | Record<string, unknown>[] | null | undefined;
  const jobObj = Array.isArray(jobRaw) ? jobRaw[0] : jobRaw;
  let job: RoleFitProjection['job'];
  if (jobObj && typeof jobObj === 'object') {
    const cr = jobObj.client as Record<string, unknown> | Record<string, unknown>[] | undefined;
    const c = Array.isArray(cr) ? cr[0] : cr;
    job = {
      id: String(jobObj.id),
      title: String(jobObj.title ?? ''),
      client_id: String(jobObj.client_id ?? ''),
      client:
        c && typeof c === 'object'
          ? { id: String((c as { id: unknown }).id), name: String((c as { name: unknown }).name ?? '') }
          : undefined,
    };
  }
  const candRaw = data.candidate as Record<string, unknown> | Record<string, unknown>[] | null | undefined;
  const cand = Array.isArray(candRaw) ? candRaw[0] : candRaw;
  let candidate: RoleFitProjection['candidate'];
  if (cand && typeof cand === 'object') {
    candidate = {
      id: String(cand.id),
      full_name: String(cand.full_name ?? ''),
      email: (cand.email as string) ?? null,
    };
  }
  return {
    id: String(data.id),
    org_id: String(data.org_id),
    candidate_id: String(data.candidate_id),
    job_id: String(data.job_id),
    topology: data.topology as RoleFitTopology,
    fitment_score: Number(data.fitment_score),
    coverage_score: Number(data.coverage_score),
    ats_overall: Number(data.ats_overall),
    ats_rating: String(data.ats_rating ?? ''),
    projected_match_min: data.projected_match_min != null ? Number(data.projected_match_min) : null,
    projected_match_max: data.projected_match_max != null ? Number(data.projected_match_max) : null,
    match_probability: data.match_probability != null ? Number(data.match_probability) : null,
    analysis_json: (data.analysis_json as Record<string, unknown>) ?? {},
    created_by: data.created_by != null ? String(data.created_by) : null,
    created_at: String(data.created_at),
    updated_at: String(data.updated_at),
    candidate,
    job,
  };
}

export const rolefitProjectionService = {
  async list(orgId: string): Promise<RoleFitProjection[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_rolefit_projections')
      .select(SELECT_LIST)
      .eq('org_id', orgId)
      .order('updated_at', { ascending: false })
      .limit(2000);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
  },

  async getByPair(orgId: string, candidateId: string, jobId: string): Promise<RoleFitProjection | null> {
    const { data, error } = await supabase
      .from('ggj_b2b_rolefit_projections')
      .select(SELECT_LIST)
      .eq('org_id', orgId)
      .eq('candidate_id', candidateId)
      .eq('job_id', jobId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    return mapRow(data as Record<string, unknown>);
  },

  async upsertPayload(payload: Record<string, unknown>): Promise<RoleFitProjection> {
    const { data, error } = await supabase
      .from('ggj_b2b_rolefit_projections')
      .upsert(payload, { onConflict: 'org_id,candidate_id,job_id' })
      .select(SELECT_LIST)
      .single();
    if (error) throw new Error(error.message);
    return mapRow(data as Record<string, unknown>);
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_rolefit_projections').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },
};
